import { HTMLAttributes, ReactNode } from 'react';

import styled from 'styled-components';

import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconEndpoint } from '@components/icons/endpoint';
import { IconFilter } from '@components/icons/filter';
import { IconForm } from '@components/icons/form';
import { IconTeam } from '@components/icons/team';
import { IconWebhook } from '@components/icons/webhook';
import { ListCard } from '@components/list-card';
import { createBooleanStore } from '@stores/create-boolean-store';
import { pxToRem } from '@utils/converters';

export const TriggerIcon = ({ bottomPaddingOffset }: { bottomPaddingOffset?: number }) => {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: -30 - (bottomPaddingOffset ?? 0),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: pxToRem(152),
        width: '100%',
      }}
    >
      <IconFilter color="uiTrans20" size={20} />
    </div>
  );
};

export type TriggerCardProps = {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  actions?: ReactNode;
  iconRight?: ReactNode;
  onClick?: () => void;
} & HTMLAttributes<HTMLDivElement>;

export function TriggerCard({
  icon,
  title,
  description,
  actions,
  iconRight,
  onClick,
  ...rest
}: TriggerCardProps) {
  return (
    <StyledTriggerCard
      icon={icon}
      title={title}
      description={description}
      actions={actions}
      onClick={onClick}
      iconRight={iconRight}
      {...rest}
    />
  );
}

export const TriggerLogListCard = styled(ListCard)({
  height: pxToRem(56),
});

export const ManualTriggerIcon = () => {
  return <IconFancyContainer variant="teal" hasBorder icon={<IconTeam color="elementTeal200" />} />;
};

export const WebhookTriggerIcon = () => {
  return (
    <IconFancyContainer
      variant="orange"
      hasBorder
      icon={<IconWebhook color="elementOrange100" />}
    />
  );
};

export const ScheduledPollingTriggerIcon = () => {
  return (
    <IconFancyContainer
      variant="indigo"
      hasBorder
      icon={<IconEndpoint color="elementIndigo100" />}
    />
  );
};

export const JourneyFormTriggerIcon = () => {
  return (
    <IconFancyContainer variant="green" hasBorder icon={<IconForm color="elementGreen200" />} />
  );
};

export const ApiTriggerIcon = () => {
  return <IconFancyContainer variant="blue" hasBorder icon={<IconApi color="elementBlue100" />} />;
};

const StyledTriggerCard = styled(ListCard).attrs({
  space: 2,
})({
  padding: pxToRem(12),
});

const triggersCollapsedStore = createBooleanStore('journey-triggers-collapsed', false);

export const useTriggersCollapsedStore = () => {
  const { useStore, toggle } = triggersCollapsedStore;
  const isCollapsed = useStore((state) => state.value);

  return {
    isCollapsed,
    toggleCollapsed: toggle,
  };
};
